import React, { useState, useRef, useEffect } from 'react';
import { mockProjects } from '../data/mockData';

/* ════════ Participate in build ════════
   已从 /redesign 复制版转正（原 ProjectsSectionRedesign，组件名还原）。
   编号统一为 Nº 01 monospace（与 FORM DL-9 / REF NO. 同语言）；
   首卡小字行带 · Field Work；数字滚动/接龙逻辑不变。 */

const fmt = (n) => n.toLocaleString('en-US');
const pad2 = (n) => String(n).padStart(2, '0');

/* 数字滚动：进入视口后从 0 递增到目标值（easeOut 减速）。
   实时接口数据替换目标值时，从当前显示值平滑续滚，不跳变。
   active=false 时暂不起跑（等前序数字滚完再放行）；
   onDone 在本轮滚动落定时回调。
   文本走 rAF 直写 DOM，不走 React state（避免每帧重渲染）。 */
const RollingNumber = ({ value, duration = 1800, active = true, onDone }) => {
  const elRef = useRef(null);
  const curRef = useRef(0);   // 当前显示值（续滚起点）
  const doneRef = useRef(false); // 是否已起跑过（决定全程/短程时长）
  const rafRef = useRef(0);
  const doneCbRef = useRef(onDone);
  doneCbRef.current = onDone;

  useEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;
    if (typeof value !== 'number') {
      el.textContent = '—';
      return undefined;
    }

    const run = () => {
      const from = curRef.current;
      if (from === value) {
        el.textContent = fmt(value);
        return;
      }
      const dur = doneRef.current ? 900 : duration; // 首次全程，后续（实时值替换）短程
      doneRef.current = true;
      const t0 = performance.now();
      cancelAnimationFrame(rafRef.current);
      const step = (now) => {
        const k = Math.min((now - t0) / dur, 1);
        const e = 1 - Math.pow(1 - k, 3);
        curRef.current = Math.round(from + (value - from) * e);
        el.textContent = fmt(curRef.current);
        if (k < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else if (doneCbRef.current) {
          doneCbRef.current();
        }
      };
      rafRef.current = requestAnimationFrame(step);
    };

    // 进入视口才起跑；已在视口内（如刷新后直接定位到区块）立即起跑
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          io.disconnect();
          run();
        }
      },
      { threshold: 0.5 }
    );
    if (active) io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, active]);

  return <span ref={elRef}>0</span>;
};

const EditorialProject = ({ project, index, total, live }) => {
  const [membersDone, setMembersDone] = useState(false);
  const stats = project.discord
    ? { ...project.discord, ...(live || {}) }
    : null;
  // 总人数缺数（显示 —）时无接龙可言，online 行直接放行
  const onlineGo = membersDone || typeof (stats && stats.members) !== 'number';
  return (
    <div className="editorial">
      <div className="ed-topline">
        <span>Case {pad2(index + 1)} / {pad2(total)}{index === 0 ? ' · Field Work' : ''}</span>
        <span>Participate in build</span>
      </div>

      <div className="ed-grid">
        <div className="ed-main">
          <div className="ed-num">Nº {pad2(index + 1)}</div>
          <h3 className="ed-title">{project.name}</h3>
          <div className="ed-rolerule">
            <span className="ed-role">{project.role}</span>
            <span className="ed-rule"></span>
          </div>
          <p className="ed-slogan bitcount-grid-single">{project.slogan}</p>
          <ul className="ed-contribs">
            {project.contributions.map((c, i) => (
              <li key={i}>↳&nbsp;&nbsp;{c}</li>
            ))}
          </ul>
          <div className="ed-links">
            {project.twitter && (
              <a href={project.twitter} target="_blank" rel="noopener noreferrer">
                {project.twitter.replace('https://', '')}
              </a>
            )}
            {project.twitter && project.website && <span>·</span>}
            {project.website && (
              <a href={project.website} target="_blank" rel="noopener noreferrer">
                {project.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </a>
            )}
          </div>
        </div>

        {stats && (
          <div className="ed-side">
            <div className="ed-stat-num goldman-regular">
              <RollingNumber
                value={typeof stats.members === 'number' ? stats.members : null}
                onDone={() => setMembersDone(true)}
              />
            </div>
            <div className="ed-stat-label">Discord members</div>
            <div className={`ed-stat-online ${onlineGo ? 'show' : ''}`}>
              <span className="ed-live-dot"></span>
              {typeof stats.online === 'number' ? (
                <>
                  <RollingNumber value={stats.online} active={onlineGo} duration={1200} /> online now
                </>
              ) : (
                'live'
              )}
            </div>
          </div>
        )}
      </div>

      {project.avatar && (
        <img src={project.avatar} alt="" aria-hidden="true" className="ed-watermark" />
      )}
    </div>
  );
};

const ProjectsSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  // 实时 Discord 人数（keyed by invite code）。拉取失败时保持 mockData
  // 里的静态值，页面不受影响——本地 dev 没有函数环境、纯静态部署没有
  // /api 路由，都会静默走静态兜底。
  const [liveStats, setLiveStats] = useState({});

  useEffect(() => {
    let cancelled = false;
    mockProjects.forEach((p) => {
      if (!p.discord || !p.discord.invite) return;
      fetch(`/api/discord/${p.discord.invite}`)
        .then((r) =>
          (r.headers.get('content-type') || '').includes('application/json') && r.ok
            ? r.json()
            : null
        )
        .then((d) => {
          if (cancelled || !d || typeof d.members !== 'number') return;
          setLiveStats((prev) => ({
            ...prev,
            [p.discord.invite]: { members: d.members, online: d.online },
          }));
        })
        .catch(() => {});
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <div className="projects-section" ref={sectionRef}>
      <style>{`
        .slide-up-title {
          opacity: 0;
          transform: translateY(60px);
          transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: transform, opacity;
        }
        .slide-up-title.visible {
          opacity: 1;
          transform: translateY(0);
        }
        .slide-up-content {
          opacity: 0;
          transform: translateY(60px);
          transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1);
          will-change: transform, opacity;
        }
        .slide-up-content.visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── 杂志编辑排版 ── */
        .projects-section { padding-top: 110px; }
        .editorial { position: relative; overflow: hidden; padding: 6px 4px 30px; }
        .ed-topline {
          display: flex; justify-content: space-between; align-items: baseline;
          font-family: 'Goldman', sans-serif; font-size: .72rem; letter-spacing: .3em;
          text-transform: uppercase; color: #6b7280;
          border-bottom: 2px solid #2d3436; padding-bottom: 10px;
        }
        .ed-grid { display: grid; grid-template-columns: 1fr 260px; gap: 40px; align-items: end; }
        .ed-num {
          font-family: ui-monospace, Consolas, monospace; font-weight: 400;
          font-size: 2.6rem; line-height: 1; color: #d29b3a; margin: 26px 0 10px;
          letter-spacing: .06em;
        }
        .ed-title {
          font-family: 'Bungee', sans-serif; font-weight: 400; margin: 0;
          font-size: clamp(2.6rem, 8vw, 6rem); line-height: .95; letter-spacing: .01em;
          color: transparent; -webkit-text-stroke: 2.5px #2d3436; transition: all .35s ease;
        }
        .editorial:hover .ed-title { color: #2d3436; -webkit-text-stroke: 2.5px transparent; }
        .ed-rolerule { display: flex; align-items: center; gap: 14px; margin: 14px 0 20px; }
        .ed-role {
          font-family: 'Goldman', sans-serif; font-weight: 700; font-size: .8rem;
          letter-spacing: .35em; color: #9c7516; text-transform: uppercase;
        }
        .ed-rule { flex: 1; height: 1px; background: rgba(45, 52, 54, .35); }
        .ed-slogan {
          font-size: 1.05rem; color: #4b5563; margin: 0 0 22px; line-height: 1.5;
        }
        .ed-contribs { list-style: none; margin: 0 0 24px; padding: 0; }
        .ed-contribs li {
          font-family: 'Goldman', sans-serif; font-size: .92rem; color: #2d3436; line-height: 2;
        }
        .ed-links { display: flex; gap: 10px; flex-wrap: wrap; font-family: 'Goldman', sans-serif; font-size: .82rem; }
        .ed-links a { color: #9c7516; text-decoration: underline; text-underline-offset: 4px; }
        .ed-links span { color: #9ca3af; }
        .ed-side { padding-bottom: 8px; text-align: right; }
        .ed-stat-num { font-weight: 700; font-size: 2.6rem; line-height: 1; color: #2d3436; }
        .ed-stat-label {
          font-family: 'Goldman', sans-serif; font-size: .72rem; letter-spacing: .3em;
          text-transform: uppercase; color: #9c7516; margin-top: 8px;
        }
        .ed-stat-online {
          font-family: 'Goldman', sans-serif; font-size: .78rem; color: #6b7280;
          margin-top: 14px; display: flex; justify-content: flex-end; align-items: center; gap: 7px;
          opacity: 0; transform: translateY(7px);
          transition: opacity .5s ease .15s, transform .5s ease .15s;
        }
        .ed-stat-online.show { opacity: 1; transform: none; }
        .ed-live-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #5ea25a;
          box-shadow: 0 0 0 3px rgba(94, 162, 90, .18);
        }
        .ed-watermark {
          position: absolute; right: -40px; bottom: -30px;
          width: 300px; border-radius: 40px;
          opacity: .08; pointer-events: none; transition: all .5s ease;
          mix-blend-mode: multiply; filter: saturate(.8);
          transform: rotate(-6deg);
        }
        .editorial:hover .ed-watermark { opacity: .13; transform: rotate(-6deg) scale(1.04); }

        @media (max-width: 767px) {
          .editorial { padding-bottom: 20px; }
          .ed-topline { font-size: .6rem; letter-spacing: .2em; }
          .ed-grid { grid-template-columns: 1fr; gap: 24px; }
          .ed-num { font-size: 1.8rem; margin-top: 18px; }
          .ed-title { font-size: clamp(2.4rem, 11vw, 3.4rem); -webkit-text-stroke-width: 1.8px; }
          .ed-slogan { font-size: .95rem; margin-bottom: 16px; }
          .ed-contribs li { font-size: .85rem; line-height: 1.9; }
          .ed-side { text-align: left; padding-bottom: 0; }
          .ed-stat-num { font-size: 2rem; }
          .ed-stat-online { justify-content: flex-start; }
          .ed-watermark { width: 170px; right: -20px; bottom: -14px; border-radius: 24px; opacity: .06; }
        }
      `}</style>
      <div className="max-w-6xl mx-auto px-8">
        <h2 className={`bungee-regular text-4xl md:text-5xl font-black text-gray-800 text-center mb-14 slide-up-title ${isVisible ? 'visible' : ''}`}>
          Participate in build
        </h2>

        <div className={`slide-up-content ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
          {mockProjects.map((project, index) => (
            <EditorialProject
              key={project.name}
              project={project}
              index={index}
              total={mockProjects.length}
              live={project.discord ? liveStats[project.discord.invite] : undefined}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectsSection;
